/**
 * Payment domain types.
 *
 * This file holds only what has no other home. The payment vocabularies proper
 * live elsewhere and this file must not restate them (AGENTS.md Rule #7c):
 *
 *   coarse checkout selection  CHECKOUT_METHODS      ./payment-method.types
 *   granular resolved code     PAYMENT_METHOD_CODES  ./payment-method.types
 *   provider kind              PAYMENT_PROVIDER_TYPES ./payment-method.types
 *   sales channel              SALES_CHANNELS        ./payment-method.types
 *   catalogue fee kind         PAYMENT_FEE_TYPES     ./payment-method.types
 *   payment status (HTTP)      PaymentStatusSchema   @autopia/api-contracts
 *   payment status (Kafka)     PaymentStatusSchema   @autopia/event-schemas
 *   payment status (database)  enum PaymentStatus    payment-service schema.prisma
 *   provider                   PaymentGatewaySchema  @autopia/api-contracts
 *
 * Until #824 this file also declared `PaymentMethod`, `PaymentStatus` and
 * `PaymentGateway`. All three matched nothing: `PaymentStatus` had `completed`
 * and `disputed` and was missing `paid` — the most common terminal state a
 * payment reaches — and `PaymentGateway` said `bank_edc` where Prisma,
 * api-contracts and event-schemas all say `manual`. They were imported by
 * nothing, so nothing broke; the hazard was that `@autopia/shared-types` is the
 * obvious place to import a domain type FROM, so the first person to do so
 * would have got a union that can never match a real payment. That is the same
 * defect #737 fixed one package over.
 */

// ============================================================================
// Sales Channel — moved, see ./payment-method.types
// ============================================================================

/**
 * `PaymentChannel` used to be declared here as
 * `'online' | 'kiosk' | 'ticket_booth' | 'reseller'`. #781 removed it. The
 * vocabulary is now `SALES_CHANNELS` / `SalesChannel` in
 * `./payment-method.types`, beside the rest of the payment configuration
 * vocabularies.
 *
 * ## Why the removal was safe, having been billed as unsafe
 *
 * ADR-036 §2c and `payment-configuration.md` §13.5c both flagged this as a
 * breaking value change needing a Rule #7b downstream pass and a data migration:
 * two members renamed (`online` → `WEB`, `ticket_booth` → `BOOTH`), all recased,
 * one added, "against a type consumed at `payment.types.ts:57` and `:183` and
 * persisted in existing rows".
 *
 * Those line references were to an earlier revision of this file. As of #781 the
 * type had **no importer anywhere in the monorepo** — `rg "PaymentChannel"`
 * across `packages/`, `services/` and `apps/` returns only its own declaration
 * and `apps/web/src/components/payment/virtual-account/bank-data.ts`, which is an
 * unrelated third declaration of the same name (`'atm' | 'mobile' | 'internet'`,
 * the bank channel used to pay a virtual account) that imports nothing from this
 * package. No column stores it and no schema derives from it.
 *
 * So there were no downstream links to walk and no rows to migrate: this was the
 * same shape of orphan as the three `PaymentStatus`/`PaymentGateway`/
 * `PaymentMethod` declarations #824 removed from this file, and it is recorded
 * here rather than deleted silently so the next reader does not re-derive the
 * migration that turned out not to be needed.
 *
 * The renamed vocabulary is not a drop-in replacement regardless — `ALL` is a
 * wildcard sentinel with no equivalent here — so anything that does start
 * carrying a channel must read the notes on `SALES_CHANNELS`, not assume a
 * mapping.
 */

// ============================================================================
// Refunds
// ============================================================================

/**
 * Refund lifecycle states — SINGLE SOURCE OF TRUTH.
 *
 * `RefundStatusSchema` in @autopia/api-contracts derives from this list.
 * Reconciled against the database rather than invented: payment-service's
 * `schema.prisma:127` documents the column as
 * `pending, approved, completed, rejected, failed`, which api-contracts already
 * matched. The version previously declared here had `processing` and lacked
 * `approved`, so it described a lifecycle the service does not implement.
 *
 * Not to be confused with `AdminRefundStatusSchema` (@autopia/api-contracts),
 * the admin console's own uppercase workflow vocabulary — a genuinely different
 * set of states, kept in step by
 * `apps/admin/src/lib/__tests__/refund-status-vocabulary.test.ts`.
 */
export const REFUND_STATUSES = [
  'pending',
  'approved',
  'completed',
  'rejected',
  'failed',
] as const;

export type RefundStatus = (typeof REFUND_STATUSES)[number];

export type RefundType = 'full' | 'partial' | 'void' | 'chargeback';

export type RefundReason =
  | 'customer_request'
  | 'event_cancelled'
  | 'duplicate_payment'
  | 'fraud'
  | 'technical_error'
  | 'policy_exception'
  | 'chargeback';

export type CardBrand = 'visa' | 'mastercard' | 'jcb' | 'amex';

// ============================================================================
// Credit Types (Wristband Credits)
// ============================================================================

export type CreditTransactionType =
  | 'purchase'
  | 'deduction'
  | 'refund'
  | 'adjustment'
  | 'expiry'
  | 'transfer_out'
  | 'transfer_in'
  | 'void';

export type CreditSourceType =
  | 'kiosk_topup'
  | 'online_topup'
  | 'ticket_bundle'
  | 'attraction_use'
  | 'refund'
  | 'admin_adjustment'
  | 'expiry'
  | 'transfer'
  | 'compensation'
  | 'promotion';

// ============================================================================
// Midtrans Types
//
// Shared between apps/web (Snap.js popup result) and payment-service
// (webhook notification + status-query response). These are 3 distinct
// Midtrans API surfaces, not one duplicated type -- MidtransTransactionCore
// below is only the subset of fields genuinely identical across all 3; each
// consumer extends it with its own additional fields.
// ============================================================================

export type MidtransTransactionStatus =
  | 'capture'
  | 'settlement'
  | 'pending'
  | 'deny'
  | 'cancel'
  | 'expire'
  | 'refund'
  | 'partial_refund'
  | 'chargeback'
  | 'authorize';

export type MidtransFraudStatus = 'accept' | 'deny' | 'challenge';

/** Fields present, with matching required/optional status, across SnapResult, MidtransNotificationPayload, and TransactionStatusResponse. */
export interface MidtransTransactionCore {
  transaction_id: string;
  order_id: string;
  gross_amount: string;
  payment_type: string;
  transaction_status: MidtransTransactionStatus;
  fraud_status?: MidtransFraudStatus;
  status_code: string;
  transaction_time: string;
  settlement_time?: string;
  expiry_time?: string;
  va_numbers?: Array<{ bank: string; va_number: string }>;
  payment_amounts?: Array<{ amount: string; paid_at: string }>;
  masked_card?: string;
  bank?: string;
  approval_code?: string;
}
